import { parse as dateFnsParse } from 'date-fns';
import { type FormikTouched } from 'formik';

import {
  DATE_FORMATS,
  FIELD_TYPES,
  LAYOUT_TYPES,
} from '../../../shared/constants';
import {
  type Field,
  type FieldElement,
  type FormValues,
} from '../../../shared/types';
import { isField, isRadioField, isSpacerField } from '../../../shared/utils';
import {
  convertToPickerDateFormat,
  validateManualDateInput,
} from '../../../shared/utils/dateFormatUtils';
import {
  evaluateDateExpression,
  evaluateExpressionValue,
} from '../../ExpressionBuilder/utils';
import { getRequiredValidation } from '../data-layer/validations';
import { type EntityInitialData } from '../data-store';

const parseCommaSeparated = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const parseBool = (v: unknown): boolean => {
  if (typeof v === 'boolean') {
    return v;
  }

  if (typeof v === 'number') {
    return v === 1;
  }

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();

    return s === 'true' || s === '1';
  }

  return false;
};

/** Fields that do not bind to Formik values in MetadataForm. */
export const shouldManageVisibilityFormValue = (field: Field): boolean => {
  if (isSpacerField(field)) {
    return false;
  }

  if (
    field.fieldType === FIELD_TYPES.DATATABLE ||
    field.fieldType === FIELD_TYPES.RICHTEXT
  ) {
    return false;
  }

  return true;
};

export const collectFormFieldsForVisibility = (
  elements: FieldElement[]
): Field[] => {
  const out: Field[] = [];

  const walk = (els: FieldElement[]): void => {
    els.forEach((el) => {
      if (isField(el) && shouldManageVisibilityFormValue(el)) {
        out.push(el);
      }

      if (
        (el.type === LAYOUT_TYPES.SECTION || el.type === LAYOUT_TYPES.ROW) &&
        el.children
      ) {
        walk(el.children);
      }
    });
  };

  walk(elements);

  return out;
};

/** Empty value when the field is cleared due to visibility hide. */
export const getClearedFieldValue = (field: Field): unknown => {
  switch (field.fieldType) {
    case FIELD_TYPES.DATE:
      return null;
    case FIELD_TYPES.MULTISELECT:
    case FIELD_TYPES.CHECKBOX:
      return [];
    case FIELD_TYPES.SINGLESELECT:
      return undefined;
    case FIELD_TYPES.RADIO:
      return null;
    case FIELD_TYPES.NUMBER:
      return '';

    case FIELD_TYPES.BOOLEAN: {
      const isRequired = getRequiredValidation(field) !== undefined;

      return isRequired ? null : false;
    }

    case FIELD_TYPES.TERMSANDCONDITIONS:
      return false;
    case FIELD_TYPES.PASSWORD:
    case FIELD_TYPES.SENSITIVE:
      return '';
    default:
      return '';
  }
};

const parseStaticDefaultFromConfiguredValue = (
  field: Field,
  raw: string
): unknown => {
  if (field.fieldType === FIELD_TYPES.SINGLESELECT) {
    return raw.trim() === '' ? undefined : raw;
  }

  if (field.fieldType === FIELD_TYPES.MULTISELECT) {
    return raw.trim() === '' ? [] : parseCommaSeparated(raw);
  }

  if (field.fieldType === FIELD_TYPES.CHECKBOX) {
    return raw.trim() === '' ? [] : parseCommaSeparated(raw);
  }

  if (isRadioField(field)) {
    return raw.trim() === '' ? null : raw;
  }

  if (field.fieldType === FIELD_TYPES.DATE) {
    if (!raw.trim()) {
      return null;
    }

    const defaultValue = raw;
    const advConfig =
      'advConfig' in field
        ? (field.advConfig as { dateFormat?: string } | undefined)
        : undefined;
    const pickerFormat = convertToPickerDateFormat(advConfig?.dateFormat ?? '');
    const isValid = validateManualDateInput(defaultValue, pickerFormat);
    let parsed: Date | null = isValid
      ? dateFnsParse(defaultValue, pickerFormat, new Date())
      : null;

    // Fallback: if the default value doesn't match the configured format,
    // try all known date formats (handles DA forms where the backend may
    // provide values in a different format than the field's configured format)
    if (!parsed) {
      const allPickerFormats = Object.values(DATE_FORMATS).map(
        (fmt) => fmt.picker
      );

      parsed = allPickerFormats.reduce<Date | null>(
        (found, fallbackFormat) => {
          if (found || fallbackFormat === pickerFormat) {
            return found;
          }

          if (!validateManualDateInput(defaultValue, fallbackFormat)) {
            return null;
          }

          return dateFnsParse(defaultValue, fallbackFormat, new Date());
        },
        null
      );
    }

    return parsed;
  }

  if (field.fieldType === FIELD_TYPES.NUMBER) {
    return raw.trim() === '' ? null : Number(raw);
  }

  if (field.fieldType === FIELD_TYPES.BOOLEAN) {
    const isRequired = getRequiredValidation(field) !== undefined;

    if (isRequired) {
      return null;
    }

    return parseBool(raw);
  }

  if (field.fieldType === FIELD_TYPES.TERMSANDCONDITIONS) {
    return parseBool(raw);
  }

  return raw;
};

export interface RestoreFieldContext {
  values: FormValues;
  touched: FormikTouched<FormValues>;
  entityData: EntityInitialData | null;
}

/**
 * Restore order: configured expression default, then configured static default,
 * then Formik initial value, then cleared baseline.
 */
export const resolveRestoredFieldValue = (
  field: Field,
  formikInitialValues: FormValues,
  ctx: RestoreFieldContext
): unknown => {
  const dv = field.defaultValue;

  if (dv?.isExpression && dv.value?.trim()) {
    if (field.fieldType === FIELD_TYPES.DATE) {
      return evaluateDateExpression(dv.value, {
        values: ctx.values,
        entityData: ctx.entityData,
      });
    }

    return evaluateExpressionValue(dv.value, {
      values: ctx.values,
      touched: ctx.touched,
      entityData: ctx.entityData,
    });
  }

  if (dv && !dv.isExpression) {
    const raw =
      dv.value !== undefined && dv.value !== null ? String(dv.value) : '';

    const skipNonConfiguredEmptyStatic =
      raw.trim() === '' &&
      (field.fieldType === FIELD_TYPES.TEXT ||
        field.fieldType === FIELD_TYPES.TEXTAREA ||
        field.fieldType === FIELD_TYPES.PASSWORD ||
        field.fieldType === FIELD_TYPES.SENSITIVE ||
        field.fieldType === FIELD_TYPES.SINGLESELECT);

    if (!skipNonConfiguredEmptyStatic) {
      return parseStaticDefaultFromConfiguredValue(field, raw);
    }
  }

  if (
    Object.prototype.hasOwnProperty.call(formikInitialValues, field.fieldName)
  ) {
    return formikInitialValues[field.fieldName];
  }

  return getClearedFieldValue(field);
};
